import { useState, useEffect } from 'react';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { supabase } from '../lib/supabaseClient';
import Header from '../components/Header';
import Button from '../components/Button';
import SuccessModal from '../components/SuccessModal';

interface FormErrors {
  currentPassword?: string;
  newEmail?: string;
  confirmEmail?: string;
  general?: string;
}

export default function ChangeEmail() {
  const { navigateTo, showToast } = useApp();
  const { refreshProfile } = useAuth();
  const { user, isLoggedOut, isPreviewMode } = useEffectiveUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isLoggedOut || !user) {
      navigateTo('profile-settings');
    }
  }, [isLoggedOut, user, navigateTo]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = 'Please enter your current password.';
    }

    if (!newEmail.trim()) {
      newErrors.newEmail = 'Please enter your new email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      newErrors.newEmail = 'Please enter a valid email address.';
    } else if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      newErrors.newEmail = 'New email must be different from your current email.';
    }

    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your new email address.';
    } else if (newEmail !== confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangeEmail = async () => {
    setErrors({});

    if (!validate()) {
      return;
    }

    if (isPreviewMode) {
      showToast('Preview mode: Email change disabled');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!user?.email) {
        setErrors({ general: 'No current email found. Please try signing in again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        console.error('Reauthentication error:', signInError);
        setErrors({ general: 'The password you entered is incorrect. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (updateError) {
        console.error('Email update error:', updateError);

        if (updateError.message.includes('already') || updateError.message.includes('exists') || updateError.message.includes('taken')) {
          setErrors({ general: 'This email is already linked to another account.' });
        } else {
          setErrors({ general: updateError.message || 'Failed to update email. Please try again.' });
        }

        setIsSubmitting(false);
        return;
      }

      setCurrentPassword('');
      setNewEmail('');
      setConfirmEmail('');
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Unexpected error during email update:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const handleModalClose = async () => {
    setShowSuccessModal(false);
    await refreshProfile();
    navigateTo('profile-settings');
  };

  const handleCancel = () => {
    navigateTo('profile-settings');
  };

  const isFormValid = currentPassword.trim() && newEmail.trim() && confirmEmail.trim() && newEmail === confirmEmail;

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title="Change email"
        showBackButton
        onBackClick={() => navigateTo('profile-settings')}
      />

      <div className="px-6 py-6">
        <div className="max-w-[600px] mx-auto">
          <div className="bg-[#181818] rounded-xl border border-[#282828] p-6" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-[#50B3A8]" />
              <h2 className="text-[17px] font-semibold text-[#F5F5F5]">
                Update your email address
              </h2>
            </div>

            <p className="text-[11px] text-[#606060] mb-6 leading-relaxed">
              Update the email you use to sign in and receive civicbeat updates.
            </p>

          {errors.general && (
            <div className="mb-6 p-4 bg-[#FF6F61]/10 border border-[#FF6F61]/20 rounded-xl">
              <p className="text-[#FF6F61] text-sm">{errors.general}</p>
            </div>
          )}

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  Current password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      if (errors.currentPassword) {
                        setErrors({ ...errors, currentPassword: undefined });
                      }
                    }}
                    placeholder="Enter your password"
                    className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 pr-12 text-[15px] border ${
                      errors.currentPassword ? 'border-[#FF6F61]' : 'border-[#282828]'
                    } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.currentPassword}</p>
                )}
              </div>

              {/* New Email */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  New email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    if (errors.newEmail) {
                      setErrors({ ...errors, newEmail: undefined });
                    }
                  }}
                  placeholder="Enter new email address"
                  className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 text-[15px] border ${
                    errors.newEmail ? 'border-[#FF6F61]' : 'border-[#282828]'
                  } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                />
                {errors.newEmail && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.newEmail}</p>
                )}
              </div>

              {/* Confirm Email */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  Confirm new email
                </label>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => {
                    setConfirmEmail(e.target.value);
                    if (errors.confirmEmail) {
                      setErrors({ ...errors, confirmEmail: undefined });
                    }
                  }}
                  placeholder="Confirm new email address"
                  className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 text-[15px] border ${
                    errors.confirmEmail ? 'border-[#FF6F61]' : 'border-[#282828]'
                  } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                />
                {errors.confirmEmail && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.confirmEmail}</p>
                )}
              </div>

              <div className="pt-4">
                <Button
                  fullWidth
                  onClick={handleChangeEmail}
                  disabled={!isFormValid || isSubmitting}
                  variant="primary"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Email'
                  )}
                </Button>

                <div className="mt-2">
                  <Button
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    variant="tertiary"
                    fullWidth
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onDismiss={handleModalClose}
        title="Email updated"
        message="Your email has been successfully updated. You'll now use this address to sign in and receive civicbeat updates."
      />
    </div>
  );
}
